import _ from 'underscore';

import { FeatureFlags } from '~src/ts/FeatureFlags';

import { getExplorableItemDefinition } from '../../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { ServerFilterClauses } from '../../../ts/helpers/ServerFilterClauses';
import { FilterFieldNames,IFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { ContentFilterKeyName } from '../../models/ContentFilters';
import { ExplorableItemType } from '../../models/ContentType';
import { MixedContentType } from '../../models/ContentTypes';
import { FilterMap } from '../../state/ContentExplorerState';
import { isRecentsSpace,isSharedWithMeSpace, isSingleCollectionSpace, SpaceInfo } from '../../state/SpaceInfo';
import { getSharedWithMeContentTypeFilterClauses, hasMultipleContentTypes, removeFilterClauseFromFilters } from './FilterHelpers';

export function getFilterClausesForTypes(mixedContentFiltersMap: FilterMap,
                                         requestedContentTypes: ReadonlyArray<ExplorableItemType>,
                                         appStateContentTypes: ReadonlyArray<ExplorableItemType>,
                                         projectId: string,
                                         spaceInfo: SpaceInfo
): IFilterClause[] {

  if (!mixedContentFiltersMap) {
    return [];
  }

  if (isSharedWithMeSpace(spaceInfo)) {
    // SharedWithMe space works a bit differently than Favorites and Explorer space.
    // In SwM, we issue same command with contenttype in <requestedcontenttype> clause
    return getSharedWithMeContentTypeFilterClauses(requestedContentTypes);
  }

  let filterKeysToKeep: ContentFilterKeyName[] = [];
  let defaultFilters = {};
  const explorableItemDefinition = getExplorableItemDefinition(requestedContentTypes[0]);

  if (hasMultipleContentTypes(requestedContentTypes) /*Requesting mixed content*/) {
    filterKeysToKeep = MixedContentType.filterKeys;
    // no default filters for mixed content

    if (!isRecentsSpace(spaceInfo) && !isSingleCollectionSpace(spaceInfo)) {
      // recents, with metricsSpaceEnabled and collection space are the only mixed content space that allows filtering by tags
      filterKeysToKeep = _.reject(filterKeysToKeep, (filter) => filter === 'tag');
    }
  } else if (hasMultipleContentTypes(appStateContentTypes)) {
    // TFSID:844413. This handles the case when we're querying for a specific content count
    // but are displaying mixed content.  So we want to use the default filters
    // for the specific content type and the mixed content filter keys.
    // So that the content count matches the number of items displayed
    defaultFilters = explorableItemDefinition.defaultFilters(projectId);
    filterKeysToKeep = MixedContentType.filterKeys;

    if (!isRecentsSpace(spaceInfo) && !isSingleCollectionSpace(spaceInfo)) {
      // recents, with metricsSpaceEnabled and collection space are the only mixed content space that allows filtering by tags
      filterKeysToKeep = _.reject(filterKeysToKeep, (filter) => filter === 'tag');
    }
  } else {
    filterKeysToKeep = explorableItemDefinition.contentFilterKeys;
    defaultFilters = explorableItemDefinition.defaultFilters(projectId);
  }

  const typeOnlyFilters = _.pick(mixedContentFiltersMap, filterKeysToKeep);
  const typeOnlyFiltersWithDefaults = _.defaults(typeOnlyFilters, defaultFilters);
  let typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFiltersWithDefaults));

  // In most spaces, when viewing just workbooks we call "getWorkbooks" and don't want a content-type fiter but we do
  // if viewing mixed content.
  // In Collections Space, when viewing just workbooks OR mixed content, we always call "/collections/<luid>/items" and
  // always want the content type filter.
  if (hasMultipleContentTypes(requestedContentTypes) || (isSingleCollectionSpace(spaceInfo) && FeatureFlags.collectionsGAEnabled)) {
    typeOnlyFiltersWithDefaultAsArray.push(ServerFilterClauses.ContentType(requestedContentTypes));
  }

  // This could go in the getCollectionItemsResourceQuery saga but putting it here makes testing work better
  if (isSingleCollectionSpace(spaceInfo) && FeatureFlags.collectionsGAEnabled) {
    typeOnlyFiltersWithDefaultAsArray = removeFilterClauseFromFilters(typeOnlyFiltersWithDefaultAsArray, FilterFieldNames.topLevelProject);

    // project id nor parentProjectId doesn't make sense for collection items
    typeOnlyFiltersWithDefaultAsArray = removeFilterClauseFromFilters(typeOnlyFiltersWithDefaultAsArray, FilterFieldNames.projectId);
    typeOnlyFiltersWithDefaultAsArray = removeFilterClauseFromFilters(typeOnlyFiltersWithDefaultAsArray, FilterFieldNames.parentProjectId);

    // TEMPORARY: TFS 1226473 - make datasourceIsPublished and more filters work and then remove this line
    typeOnlyFiltersWithDefaultAsArray = removeFilterClauseFromFilters(typeOnlyFiltersWithDefaultAsArray, FilterFieldNames.isPublished);
  }

  return typeOnlyFiltersWithDefaultAsArray;
}
